import React, { useEffect } from 'react';

interface StructuredDataProps {
  type: 'organization' | 'service' | 'product';
  page?: string;
}

export const StructuredData: React.FC<StructuredDataProps> = ({ type, page = 'home' }) => {
  const getStructuredData = () => {
    const baseOrganization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Cognitive Craft Hub",
      "alternateName": "CCH",
      "description": "Leading AI solutions company providing specialized artificial intelligence and blockchain services including AI automation, machine learning, generative AI, computer vision, predictive analytics, and distributed ledger technology.",
      "url": "https://cognitivecrafthub.com",
      "logo": "https://cognitivecrafthub.com/images/cch-logo.svg",
      "foundingDate": "2023",
      "founders": [
        {
          "@type": "Person",
          "name": "Cognitive Craft Hub Team"
        }
      ],
      "address": [
        {
          "@type": "PostalAddress",
          "addressLocality": "East Lansing",
          "addressRegion": "Michigan",
          "addressCountry": "US"
        },
        {
          "@type": "PostalAddress",
          "addressLocality": "Houston",
          "addressRegion": "Texas",
          "addressCountry": "US"
        }
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "team@cognitivecrafthub.com",
        "availableLanguage": "English"
      },
      "sameAs": [
        "https://cognitivecrafthub.com"
      ],
      "serviceArea": {
        "@type": "Country",
        "name": "United States"
      },
      "knowsAbout": [
        "Artificial Intelligence",
        "Machine Learning",
        "AI Automation",
        "Generative AI",
        "Computer Vision",
        "Predictive Analytics",
        "Natural Language Processing",
        "AI Consulting",
        "Digital Transformation",
        "Custom Software Development",
        "Cloud Computing",
        "Internet of Things",
        "Cybersecurity",
        "Healthcare Technology",
        "Blockchain Technology",
        "Distributed Ledger Technology",
        "Smart Contracts",
        "Cryptocurrency Development",
        "Robotics Automation",
        "Robotics Implementation",
        "Hospitality Robotics"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Solutions & Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Automation Solutions",
              "description": "Intelligent automation with AI agents and RPA"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Machine Learning Services",
              "description": "Custom ML models and predictive analytics"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Generative AI Solutions",
              "description": "Custom GPT implementations and content generation"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Computer Vision AI",
              "description": "Image analytics and visual data processing"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Conversational AI",
              "description": "Chatbots, virtual assistants, and voice solutions"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Blockchain Solutions",
              "description": "Smart contracts, DApps, and distributed ledger technology"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Robotics Implementation Services",
              "description": "End-to-end robotics automation consulting and implementation"
            }
          }
        ]
      }
    };

    if (type === 'organization') {
      return baseOrganization;
    }

    if (type === 'service') {
      return {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": "AI Solutions & Services",
        "description": "Comprehensive artificial intelligence solutions including AI automation, machine learning, generative AI, computer vision, predictive analytics, and custom AI development.",
        "provider": baseOrganization,
        "serviceType": "Artificial Intelligence Solutions",
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "AI Services Portfolio",
          "itemListElement": [
            {
              "@type": "Service",
              "name": "Cognitive Reach - AI Marketing",
              "description": "AI-powered digital marketing automation and optimization"
            },
            {
              "@type": "Service", 
              "name": "Cognitive Intelligence - AI Analytics",
              "description": "Advanced AI and data analytics solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Agents - AI Automation", 
              "description": "Intelligent AI agents and robotic process automation"
            },
            {
              "@type": "Service",
              "name": "Cognitive Generate - Generative AI",
              "description": "Custom generative AI implementations and content creation"
            },
            {
              "@type": "Service",
              "name": "Cognitive Vision - Computer Vision",
              "description": "Computer vision and image analytics solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Voice - Conversational AI",
              "description": "AI-powered voice solutions and chatbots"
            },
            {
              "@type": "Service",
              "name": "Cognitive Predict - Predictive Analytics",
              "description": "Machine learning-powered predictive analytics"
            },
            {
              "@type": "Service",
              "name": "Cognitive Train - AI Training",
              "description": "Comprehensive AI education and consulting"
            },
            {
              "@type": "Service",
              "name": "Cognitive Secure - AI Cybersecurity",
              "description": "AI-powered cybersecurity solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Cloud - Cloud AI",
              "description": "Scalable cloud infrastructure for AI solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive IoT - IoT AI",
              "description": "AI-powered Internet of Things solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Build - Custom AI Development",
              "description": "Custom AI software development and integration"
            },
            {
              "@type": "Service",
              "name": "Cognitive Compliance - AI Compliance",
              "description": "AI-powered regulatory and legal compliance solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Ledger - Blockchain Solutions",
              "description": "Blockchain and distributed ledger technology solutions"
            },
            {
              "@type": "Service",
              "name": "Cognitive Robotics - Robotics Implementation",
              "description": "Robotics automation consulting and implementation services for hospitality and commercial facilities"
            }
          ]
        }
      };
    }

    if (type === 'product') {
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Cognitive Care EHR System",
        "description": "Revolutionary Electronic Health Records (EHR) system designed for healthcare providers of all sizes. Scalable from solo practitioners to large hospital systems.",
        "applicationCategory": "HealthApplication",
        "operatingSystem": "Web-based",
        "offers": {
          "@type": "Offer",
          "price": "Contact for pricing",
          "priceCurrency": "USD",
          "availability": "https://schema.org/PreOrder",
          "validFrom": "2024-01-01",
          "description": "Expected launch March 2026"
        },
        "provider": baseOrganization,
        "featureList": [
          "Electronic Health Records",
          "Patient Portal",
          "Provider Portal", 
          "HIPAA Compliance",
          "Intelligent Scheduling",
          "Clinical Documentation",
          "Automated Alerts"
        ]
      };
    }

    return baseOrganization;
  };

  const structuredData = getStructuredData();

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData, null, 2);
    script.id = `structured-data-${type}`;
    
    // Remove existing script if it exists
    const existingScript = document.getElementById(`structured-data-${type}`);
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.getElementById(`structured-data-${type}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [structuredData, type]);

  return null;
};